import React, { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { TeamView, useCreateTeamMutation, useEditTeamMutation } from '../../api/client'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { toastError, toastSuccess } from '../Notification'
import { isUserAdminState } from '../../store/DomainStore'
import TeamUsersTable from './TeamUsersTable'
import TeamAccountsTable from './TeamAccountsTable'
import {
    UsersIcon,
    BuildingOfficeIcon,
    PencilIcon,
    CheckIcon,
    XMarkIcon
} from '@heroicons/react/24/outline'
import clsx from 'clsx'

interface IFormInput {
    name: string
}

type Props = {
    team?: TeamView
}

function EnhancedTeamDetailsForm(props: Props) {
    const { team } = props
    const navigate = useNavigate()
    const isAdmin = useRecoilValue(isUserAdminState)

    const [isEditing, setIsEditing] = useState(false)
    const [activeTab, setActiveTab] = useState<'users' | 'accounts'>('users')

    const [createTeamMutation, { loading: createTeamLoading }] = useCreateTeamMutation()
    const [editTeamMutation, { loading: editTeamLoading }] = useEditTeamMutation()

    const values = team ? team : undefined
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isDirty }
    } = useForm<IFormInput>({
        values
    })

    // Are we creating a new team or editing an existing one?
    const isCreateMode = !!!team

    const onSubmit: SubmitHandler<IFormInput> = async (data) => {
        if (isCreateMode) {
            await createTeamMutation({
                variables: { teamName: data.name },
                onCompleted: () => {
                    toastSuccess(`Team ${data.name} created.`)
                    navigate(-1)
                },
                onError: (error) => {
                    console.error(error)
                    toastError(`Unable to create team. ${error.message}`)
                }
            })
        } else {
            await editTeamMutation({
                variables: { teamId: team!.id, name: data.name },
                onCompleted: () => {
                    toastSuccess(`Team ${data.name} updated.`)
                    setIsEditing(false)
                },
                onError: (error) => {
                    console.error(error)
                    toastError(`Unable to update team. ${error.message}`)
                }
            })
        }
    }

    const handleCancelEdit = () => {
        reset()
        setIsEditing(false)
    }

    const tabs = [
        {
            id: 'users' as const,
            name: 'Team members',
            icon: UsersIcon
        },
        {
            id: 'accounts' as const,
            name: 'Profiles',
            icon: BuildingOfficeIcon
        }
    ]

    if (isCreateMode) {
        return (
            <div className="max-w-2xl mx-auto">
                <div className="bg-white shadow-sm rounded-lg border border-gray-200">
                    <div className="px-6 py-8">
                        <div className="text-center mb-8">
                            {/* font-sans + !font-medium override the global Merriweather
                                h1-h4 rule in index.css — app headings are Jost 500 */}
                            <h2 className="font-sans !font-medium text-[clamp(1.75rem,1.1rem+1.8vw,2.4rem)] leading-[1.1] tracking-tight text-gray-900">
                                Create new team
                            </h2>
                            <p className="mt-2 text-sm text-gray-600">
                                Set up a new team to organize users and profiles.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                            <div>
                                <label
                                    htmlFor="name"
                                    className="block text-sm font-medium text-gray-700"
                                >
                                    Team name
                                </label>
                                <div className="mt-2">
                                    <input
                                        type="text"
                                        id="name"
                                        {...register('name', {
                                            required: 'Team name is required',
                                            minLength: {
                                                value: 3,
                                                message: 'Team name must be at least 3 characters'
                                            }
                                        })}
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-arwen-500 focus:ring-arwen-500 sm:text-sm"
                                        placeholder="Enter team name..."
                                    />
                                </div>
                                {errors.name && (
                                    <p className="mt-2 text-sm text-red-600">
                                        {errors.name.message}
                                    </p>
                                )}
                            </div>

                            <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => navigate(-1)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-arwen-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={createTeamLoading}
                                    className="px-4 py-2 text-sm font-semibold text-white bg-arwen-600 border border-transparent rounded-lg shadow-sm hover:bg-arwen-700 focus:outline-none focus:ring-2 focus:ring-arwen-500 disabled:opacity-50"
                                >
                                    {createTeamLoading ? 'Creating...' : 'Create team'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="max-w-5xl mx-auto px-6">
            {/* Header */}
            <div className="bg-white shadow-sm rounded-lg border border-gray-200 mb-6">
                <div className="px-6 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="flex-shrink-0">
                                <div className="h-12 w-12 rounded-lg bg-gray-200 flex items-center justify-center">
                                    <BuildingOfficeIcon className="h-6 w-6 text-gray-800" />
                                </div>
                            </div>
                            <div className="flex-1 min-w-0">
                                {isEditing ? (
                                    <form
                                        onSubmit={handleSubmit(onSubmit)}
                                        className="flex items-center space-x-3"
                                    >
                                        <input
                                            type="text"
                                            {...register('name', {
                                                required: 'Team name is required',
                                                minLength: {
                                                    value: 3,
                                                    message:
                                                        'Team name must be at least 3 characters'
                                                }
                                            })}
                                            className="font-sans !font-medium text-[clamp(1.75rem,1.1rem+1.8vw,2.4rem)] leading-[1.1] tracking-tight text-gray-900 bg-transparent border-0 border-b-2 border-arwen-500 focus:ring-0 focus:border-arwen-600 p-0"
                                            autoFocus
                                        />
                                        <div className="flex items-center space-x-2">
                                            <button
                                                type="submit"
                                                disabled={editTeamLoading || !isDirty}
                                                className="p-2 text-green-600 hover:text-green-700 disabled:opacity-50"
                                            >
                                                <CheckIcon className="h-5 w-5" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleCancelEdit}
                                                className="p-2 text-gray-400 hover:text-gray-600"
                                            >
                                                <XMarkIcon className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </form>
                                ) : (
                                    <div className="flex items-center space-x-3">
                                        {/* Jost 500 override of the global Merriweather rule;
                                            the red full stop is the arwen dot, one per page */}
                                        <h1 className="font-sans !font-medium text-[clamp(1.75rem,1.1rem+1.8vw,2.4rem)] leading-[1.1] tracking-tight text-gray-900">
                                            {team?.name}
                                            <span aria-hidden="true" className="text-arwen-600">
                                                .
                                            </span>
                                        </h1>
                                        {isAdmin && (
                                            <button
                                                onClick={() => setIsEditing(true)}
                                                className="p-2 text-gray-400 hover:text-gray-600"
                                            >
                                                <PencilIcon className="h-5 w-5" />
                                            </button>
                                        )}
                                    </div>
                                )}
                                <p className="text-sm text-gray-500 mt-1">
                                    Team ID: {team?.id} • Created{' '}
                                    {team?.createdAt
                                        ? new Date(team.createdAt).toLocaleDateString()
                                        : 'Unknown'}
                                </p>
                                {errors.name && (
                                    <p className="mt-2 text-sm text-red-600">
                                        {errors.name.message}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => navigate(-1)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-arwen-500"
                            >
                                Back
                            </button>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                {/* Tab bar styled to match the Organisation view (OrgConfigPage.tsx) */}
                <div className="border-t border-gray-200">
                    <nav className="flex px-6" aria-label="Tabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={clsx(
                                    'border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
                                    activeTab === tab.id
                                        ? 'border-arwen-600 text-arwen-700'
                                        : 'border-transparent text-gray-800 hover:text-gray-800'
                                )}
                            >
                                {tab.name}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Content */}
            <div className="space-y-6">
                {activeTab === 'users' && <TeamUsersTable teamId={team!.id} isAdmin={isAdmin} />}
                {activeTab === 'accounts' && (
                    <TeamAccountsTable teamId={team!.id} isAdmin={isAdmin} />
                )}
            </div>
        </div>
    )
}

export default EnhancedTeamDetailsForm
